import { dismissAlert } from '@calendly/alert';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { Body, Footer, Popup } from '@calendly/ui/components/popup';
import { BodyText } from '@calendly/ui/components/typography';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TRACKING_EVENT_NAMES } from 'hooks/analytics_events/constants';
import { useTrack } from 'hooks/analytics_events/useTrack';
import { anrokSupportedCanadianProvinces, anrokSupportedCountryCodes } from './anrok_supported_countries';
import { updateTaxIdAndShippingDataStatusSelector } from './selectors';
import { setConfirmationVisibility, setConfirmedTaxInformation, setInitialValues, setPopupLocation, setShowThankYouPopup, setVisibility, updateTaxIdAndShippingAddress, validateTaxIds } from './slice';
import { dealsPageAvailableCountriesSelector, policyAvailableCountriesSelector, taxIdValidationCallSelector, taxInformationModalShouldUpdateExistingSubscriptionSelector, taxInformationModalValidatedFormDataSelector } from '../../selectors';
const sectionWrapperCss = "s19xl2xc";
const SectionWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(sectionWrapperCss, props.className)} />;
});
const Section = ({
  title,
  children
}) => <SectionWrapper>
    <BodyText weight="bold">
      {title}
    </BodyText>

    {children}
  </SectionWrapper>;
export const TaxInformationConfirmationModal = () => {
  const policyAvailableCountries = useSelector(policyAvailableCountriesSelector);
  const dealAvailableCountries = useSelector(dealsPageAvailableCountriesSelector);
  const dispatch = useDispatch();
  const track = useTrack();
  const validatedFormData = useSelector(taxInformationModalValidatedFormDataSelector);
  const {
    loading: taxIdValidationLoading
  } = useSelector(taxIdValidationCallSelector);
  const updateTaxIdAndShippingDataStatus = useSelector(updateTaxIdAndShippingDataStatusSelector);
  const shouldUpdateExistingSubscription = useSelector(taxInformationModalShouldUpdateExistingSubscriptionSelector);
  const availableCountries = () => policyAvailableCountries || dealAvailableCountries;
  const {
    streetAddress,
    streetAddressTwo,
    city,
    stateProvince,
    postalCode,
    countryCode,
    companyName,
    australiaTaxId,
    canadaFederalTaxId,
    canadaProvincialTaxId,
    taxId
  } = validatedFormData;
  const taxIds = () => {
    switch (countryCode) {
      case 'AU':
        return [australiaTaxId];
      case 'CA':
        if (anrokSupportedCanadianProvinces.includes(stateProvince)) {
          return [canadaFederalTaxId, canadaProvincialTaxId].filter(Boolean);
        }
        return [canadaFederalTaxId].filter(Boolean);
      default:
        return [taxId];
    }
  };
  const submittingTaxData = () => updateTaxIdAndShippingDataStatus.loading;
  const handleAnrokValidation = () => {
    // Only do Anrok validation via billing service if user's country is in Anrok's list of supported countries
    if (anrokSupportedCountryCodes.includes(countryCode) && taxIds().length > 0) {
      const taxIdMap = {
        australiaTaxId,
        canadaFederalTaxId,
        canadaProvincialTaxId,
        taxId
      };
      dispatch(dismissAlert()); // Dismiss alert potentially caused by Anrok issue before re-submission
      dispatch(validateTaxIds({
        taxIds: taxIds(),
        countryCode,
        postalCode,
        taxIdMap,
        validatedFormData,
        shouldUpdateExistingSubscription
      }));
    } else if (shouldUpdateExistingSubscription) {
      dispatch(updateTaxIdAndShippingAddress());
    } else {
      dispatch(setConfirmedTaxInformation(validatedFormData));
      dispatch(setConfirmationVisibility(false));
      dispatch(setVisibility(false));
      dispatch(setPopupLocation(null));
      dispatch(setShowThankYouPopup(true));
    }
  };
  const handleEdit = () => {
    track({
      name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
      properties: {
        action: 'editing_info',
        location: 'billing_page',
        modal_name: 'confirm_tax_info'
      }
    });
    dispatch(setInitialValues(validatedFormData));
    dispatch(setConfirmationVisibility(false));
  };
  const countryNameFromCode = () => availableCountries().find(ac => ac.id === countryCode).name;
  const provincialLabel = stateProvince === 'QC' ? 'ca_qc_id_label' : 'ca_provincial_id_label';

  // i18n-tasks-use t('publisher.billing.vat_id_popup.ca_qc_id_label')
  // i18n-tasks-use t('publisher.billing.vat_id_popup.ca_provincial_id_label')
  const taxIdField = () => {
    switch (countryCode) {
      case 'AU':
        return <Section title={I18n.t('publisher.billing.vat_id_popup.au_tax_id_label')}>
            {australiaTaxId}
          </Section>;
      case 'CA':
        return <>
            {canadaFederalTaxId && <Section title={I18n.t('publisher.billing.vat_id_popup.ca_federal_id_label')}>
                {canadaFederalTaxId}
              </Section>}

            {canadaProvincialTaxId && <Section title={I18n.t(`publisher.billing.vat_id_popup.${provincialLabel}`)}>
                {canadaProvincialTaxId}
              </Section>}
          </>;
      default:
        return <Section title={I18n.t('publisher.billing.vat_id_popup.tax_id_number')}>
            {taxId}
          </Section>;
    }
  };
  return <Popup onClose={() => {}} size="medium" title={I18n.t('publisher.billing.vat_id_popup.confirmation_popup.title')}>
      <Body>
        <Section title={I18n.t('publisher.billing.vat_id_popup.confirmation_popup.address')}>
          {streetAddress}
          <br />
          {streetAddressTwo && <>
              {streetAddressTwo}
              <br />
            </>}
          {city}
          {stateProvince && <>
              ,&nbsp;
              {stateProvince}
            </>}
          &nbsp;
          {postalCode}
          <br />
          {countryNameFromCode()}
        </Section>

        <Section title={I18n.t('publisher.billing.vat_id_popup.company_name')}>
          {companyName}
        </Section>

        {taxIdField()}
      </Body>
      <Footer>
        <Button decoration="secondary-outline" onClick={handleEdit}>
          {I18n.t('publisher.billing.vat_id_popup.confirmation_popup.edit')}
        </Button>

        <Button onClick={handleAnrokValidation} loading={taxIdValidationLoading || submittingTaxData()}>
          {I18n.t('publisher.billing.vat_id_popup.confirmation_popup.confirm')}
        </Button>
      </Footer>
    </Popup>;
};

require("./tax_information_confirmation_modal.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./tax_information_confirmation_modal.js");